import { Array as A, Option, pipe } from 'effect';

/** A viewport rectangle (client coordinates), the shape of DOMRect's fields we use. */
export interface Rect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

export const rectWidth = (r: Rect): number => r.right - r.left;
export const rectHeight = (r: Rect): number => r.bottom - r.top;
export const rectArea = (r: Rect): number => Math.max(0, rectWidth(r)) * Math.max(0, rectHeight(r));
export const isEmptyRect = (r: Rect): boolean => rectWidth(r) <= 0 || rectHeight(r) <= 0;

export const intersectRects = (a: Rect, b: Rect): Rect => ({
  left: Math.max(a.left, b.left),
  top: Math.max(a.top, b.top),
  right: Math.min(a.right, b.right),
  bottom: Math.min(a.bottom, b.bottom),
});

export const unionRects = (a: Rect, b: Rect): Rect => ({
  left: Math.min(a.left, b.left),
  top: Math.min(a.top, b.top),
  right: Math.max(a.right, b.right),
  bottom: Math.max(a.bottom, b.bottom),
});

const overlaps = (a: Rect, b: Rect, gap: number): boolean =>
  a.left < b.right + gap && b.left < a.right + gap && a.top < b.bottom + gap && b.top < a.bottom + gap;

const containsPoint = (r: Rect, x: number, y: number): boolean =>
  x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;

export interface PencilInput {
  readonly id: string;
  /** The visible part of the text (or of the element, for an attribute text). */
  readonly rect: Rect;
  /** Position among the pencils of the same element: 0 for its text, then its attribute texts — placed leftwards. */
  readonly slot: number;
}

export interface PencilPlacement {
  readonly id: string;
  readonly box: Rect;
  /** Overlaps an earlier pencil (or is over the cap): drawn only while its text is hovered. */
  readonly collapsed: boolean;
}

export interface LayoutOptions {
  readonly size: number;
  /** Minimum free space between two visible pencils. */
  readonly gap: number;
  readonly viewport: { readonly width: number; readonly height: number };
  /** Visible pencils beyond this count collapse. */
  readonly maxVisible: number;
}

export const DEFAULT_LAYOUT: Omit<LayoutOptions, 'viewport'> = { size: 18, gap: 2, maxVisible: 300 };

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), Math.max(min, max));

/**
 * The pencil's box: centred on the text's top-right corner (half outside, so it never hides the text's first
 * characters), moved left by its slot, flipped inside the text when it would leave the top of the viewport, and kept
 * inside the viewport.
 */
export const pencilBox = (input: PencilInput, { size, gap, viewport }: Omit<LayoutOptions, 'maxVisible'>): Rect => {
  const half = size / 2;
  const rawLeft = input.rect.right - half - input.slot * (size + gap);
  const rawTop = input.rect.top - half < 0 ? input.rect.top : input.rect.top - half;
  const left = clamp(rawLeft, 0, viewport.width - size);
  const top = clamp(rawTop, 0, viewport.height - size);
  return { left, top, right: left + size, bottom: top + size };
};

/**
 * Places pencils in the given order (document order): a pencil that would overlap an already visible one, or that
 * comes after `maxVisible` visible ones, collapses — it is drawn only while its text is hovered.
 */
export const layoutPencils = (
  inputs: ReadonlyArray<PencilInput>,
  options: LayoutOptions,
): ReadonlyArray<PencilPlacement> =>
  A.reduce(
    inputs,
    { placed: [] as ReadonlyArray<PencilPlacement>, visible: [] as ReadonlyArray<Rect> },
    (acc, input) => {
      const box = pencilBox(input, options);
      const collapsed =
        acc.visible.length >= options.maxVisible || acc.visible.some((v) => overlaps(v, box, options.gap));
      return {
        placed: [...acc.placed, { id: input.id, box, collapsed }],
        visible: collapsed ? acc.visible : [...acc.visible, box],
      };
    },
  ).placed;

/** The hovered target: the smallest rect under the point (the innermost text wins over the block around it). */
export const hoveredTarget = <T extends { readonly rect: Rect }>(
  targets: ReadonlyArray<T>,
  x: number,
  y: number,
): Option.Option<T> =>
  pipe(
    targets,
    A.filter((t) => containsPoint(t.rect, x, y)),
    A.reduce(Option.none<T>(), (best, t) =>
      Option.match(best, {
        onNone: () => Option.some(t),
        onSome: (b) => (rectArea(t.rect) < rectArea(b.rect) ? Option.some(t) : best),
      }),
    ),
  );
